import { useState } from "react";
import { Link } from "react-router-dom";
import { Scale, Search, Filter, BarChart3, TrendingUp, Info, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TopBar } from "@/components/PageShell";
import ImageSlot from "@/components/ImageSlot";

const ESTADOS = [["MG","Minas Gerais"],["SP","São Paulo"],["RJ","Rio de Janeiro"],["RS","Rio Grande do Sul"],["PR","Paraná"],["BA","Bahia"],["SC","Santa Catarina"],["GO","Goiás"],["PE","Pernambuco"],["CE","Ceará"],["ES","Espírito Santo"],["MT","Mato Grosso"],["MS","Mato Grosso do Sul"],["DF","Distrito Federal"],["PA","Pará"],["MA","Maranhão"],["PB","Paraíba"],["RN","Rio Grande do Norte"],["AL","Alagoas"],["PI","Piauí"],["SE","Sergipe"],["AM","Amazonas"],["RO","Rondônia"],["AC","Acre"],["RR","Roraima"],["AP","Amapá"],["TO","Tocantins"]];

const UF_TRIBUNAL = {
  STATE:   (uf) => `TJ${uf}`,
  FEDERAL: (uf) => ({MG:"TRF6",SP:"TRF3",RJ:"TRF2",RS:"TRF4",PR:"TRF4",SC:"TRF4",DF:"TRF1",GO:"TRF1",MT:"TRF1",MS:"TRF3",AM:"TRF1",PA:"TRF1",BA:"TRF1",ES:"TRF2",CE:"TRF5",PE:"TRF5"}[uf] || "TRF1"),
  LABOR:   (uf) => `TRT${{MG:3,SP:2,RJ:1,RS:4,PR:9,SC:12,BA:5,DF:10,CE:7,PE:6,GO:18,ES:17}[uf] || 1}`,
  ELECT:   (uf) => `TRE-${uf}`,
};

const PERIODOS = [["","Todos"],["-1a","Último ano"],["-3a","Últimos 3 anos"],["-5a","Últimos 5 anos"]];

const dateBounds = (code) => {
  if (!code) return {};
  const yrs = parseInt(code.match(/(\d+)/)?.[1] || 0, 10);
  const dt = new Date(); dt.setFullYear(dt.getFullYear() - yrs);
  return { date_from: dt.toISOString().slice(0,10) };
};

const Donut = ({ counts, testid, palette = { PROCEDENTE:"#10B981", PARCIALMENTE_PROCEDENTE:"#F59E0B", IMPROCEDENTE:"#DC2626", EXTINTO_SEM_MERITO:"#94a3b8", NAO_IDENTIFICADO:"#e2e8f0" } }) => {
  const entries = Object.entries(counts || {}).filter(([, v]) => v > 0);
  const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
  let acc = 0;
  const size = 160, r = 60, cx = 80, cy = 80, sw = 20;
  const arcs = entries.map(([k, v]) => {
    const pct = v / total;
    const angle0 = acc * 2 * Math.PI - Math.PI/2; acc += pct;
    const angle1 = acc * 2 * Math.PI - Math.PI/2;
    const x0 = cx + r * Math.cos(angle0), y0 = cy + r * Math.sin(angle0);
    const x1 = cx + r * Math.cos(angle1), y1 = cy + r * Math.sin(angle1);
    const large = pct > 0.5 ? 1 : 0;
    return <path key={k} d={`M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`} stroke={palette[k] || "#94a3b8"} strokeWidth={sw} fill="none"/>;
  });
  return (
    <div data-testid={testid} className="flex items-center gap-4">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {arcs}
        <text x={cx} y={cy-2} textAnchor="middle" fontSize="24" fontWeight="700" fill="#0B1B2F">{total}</text>
        <text x={cx} y={cy+16} textAnchor="middle" fontSize="10" fill="#64748b">julgados</text>
      </svg>
      <ul className="text-xs space-y-1">
        {entries.map(([k, v]) => (
          <li key={k} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: palette[k] || "#94a3b8" }}/>
            <span className="text-slate-700">{k.replace(/_/g," ").toLowerCase()}</span>
            <span className="font-semibold text-slate-900">{Math.round(100*v/total)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const YearlyBars = ({ series }) => {
  if (!series || !series.length) return null;
  const max = Math.max(...series.map(y => y.possibilidade_pct || 0), 1);
  return (
    <div className="flex items-end gap-2 h-24" data-testid="yearly-trend">
      {series.map(y => {
        const h = y.possibilidade_pct ? Math.max(8, (y.possibilidade_pct / max) * 80) : 4;
        return (
          <div key={y.year} className="flex-1 flex flex-col items-center gap-1">
            <div className="text-[10px] font-bold text-slate-700">{y.possibilidade_pct != null ? `${Math.round(y.possibilidade_pct)}%` : "—"}</div>
            <div className="w-full rounded-t" style={{ height: `${h}px`, background: "linear-gradient(180deg,#F5DE8C,#D4AF37)" }}/>
            <div className="text-[10px] text-slate-500">{y.year}</div>
          </div>
        );
      })}
    </div>
  );
};

/* ===== CONVERGÊNCIA COM A JURISPRUDÊNCIA ===== */

// Paleta oficial da convergência (verde clássico · dourado Hermes · vermelho marsala)
const CONV_COLORS = {
  PROCEDENTE:              "#15803D",   // verde clássico
  PARCIALMENTE_PROCEDENTE: "#C9953D",   // dourado Hermes
  IMPROCEDENTE:            "#8B1A1A",   // vermelho marsala
  EXTINTO_SEM_MERITO:      "#94A3B8",   // cinza
  NAO_IDENTIFICADO:        "#E2E8F0",   // cinza claro
};
const CONV_LABEL = {
  PROCEDENTE:              "Procedência",
  PARCIALMENTE_PROCEDENTE: "Parcialmente procedente",
  IMPROCEDENTE:            "Improcedência",
  EXTINTO_SEM_MERITO:      "Extinto sem mérito",
  NAO_IDENTIFICADO:        "Não identificado",
};

// Pizza CHEIA (não donut). Aceita destaque (foco em uma fatia)
const FullPie = ({ counts, focus, size = 220 }) => {
  const entries = Object.entries(counts || {}).filter(([, v]) => v > 0);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  if (!total) return (
    <div className="w-full flex items-center justify-center h-[220px] text-slate-400 text-sm" data-testid="conv-pie-empty">
      Sem dados suficientes para pizza
    </div>
  );
  const cx = size/2, cy = size/2, r = size/2 - 6;
  let acc = 0;
  const slices = entries.map(([k, v]) => {
    const pct = v/total;
    const a0 = acc * 2*Math.PI - Math.PI/2;
    acc += pct;
    const a1 = acc * 2*Math.PI - Math.PI/2;
    const x0 = cx + r*Math.cos(a0), y0 = cy + r*Math.sin(a0);
    const x1 = cx + r*Math.cos(a1), y1 = cy + r*Math.sin(a1);
    const large = pct > 0.5 ? 1 : 0;
    // Fatia como setor completo (M cx cy → L borda → arc → Z)
    const d = pct >= 1
      ? `M ${cx-r} ${cy} A ${r} ${r} 0 1 1 ${cx+r} ${cy} A ${r} ${r} 0 1 1 ${cx-r} ${cy} Z`
      : `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`;
    const isFocused = !focus || focus === k;
    return {
      key: k, d, pct, v,
      fill: CONV_COLORS[k] || "#94A3B8",
      opacity: isFocused ? 1 : 0.15,
      stroke: "#ffffff",
    };
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="conv-pie">
      {slices.map(s => (
        <path key={s.key} d={s.d} fill={s.fill} stroke={s.stroke} strokeWidth={2}
              style={{ opacity: s.opacity, transition: "opacity .25s" }}>
          <title>{`${CONV_LABEL[s.key] || s.key}: ${s.v} (${Math.round(s.pct*100)}%)`}</title>
        </path>
      ))}
    </svg>
  );
};

// Combina merits_counts local + stj em um único objeto
const combineMerits = (local, stj) => {
  const out = {};
  const addFrom = (src) => Object.entries(src || {}).forEach(([k, v]) => { out[k] = (out[k] || 0) + (v || 0); });
  addFrom(local); addFrom(stj);
  return out;
};

const ConvergenceCard = ({ local, stj, tribunal }) => {
  const [focus, setFocus] = useState(null); // null | "PROCEDENTE" | "PARCIALMENTE_PROCEDENTE" | "IMPROCEDENTE"
  const merged = combineMerits(local?.merits_counts, stj?.merits_counts);
  const total = Object.values(merged).reduce((s, v) => s + v, 0);
  const pct = (k) => total ? Math.round(100 * (merged[k] || 0) / total) : 0;
  const chip = (k, label, color) => {
    const active = focus === k;
    return (
      <button
        key={k}
        onClick={() => setFocus(active ? null : k)}
        data-testid={`conv-chip-${k}`}
        className={`h-9 px-4 rounded-full text-[12px] font-semibold border transition inline-flex items-center gap-2 ${active ? "text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
        style={{
          borderColor: color,
          background: active ? color : undefined,
        }}
      >
        <span className="w-2 h-2 rounded-full" style={{ background: active ? "#fff" : color }}/>
        {label} · {pct(k)}%
      </button>
    );
  };
  return (
    <div className="bg-white rounded-2xl border shadow-sm p-6 mb-6" style={{ borderColor: "#E7E5E2" }} data-testid="convergence-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] font-semibold" style={{ color: "#C9953D" }}>
            Convergência com a jurisprudência
          </div>
          <h3 className="font-serif text-2xl text-[#132238] mt-1">Como as decisões se distribuem</h3>
          <p className="text-[13px] text-slate-600 mt-1">
            Amostra unificada · <strong>{tribunal}</strong> + <strong>STJ</strong> · {total} julgado{total===1?"":"s"}
          </p>
        </div>
        {focus && (
          <button onClick={() => setFocus(null)} data-testid="conv-clear-filter"
            className="h-8 px-3 rounded-full text-[11px] border border-slate-200 text-slate-600 hover:bg-slate-50">
            Limpar filtro
          </button>
        )}
      </div>

      {/* Chips de filtro (só os 3 solicitados destacados) */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="conv-chips">
        {chip("PROCEDENTE",              "Procedência",             CONV_COLORS.PROCEDENTE)}
        {chip("PARCIALMENTE_PROCEDENTE", "Parcialmente procedente", CONV_COLORS.PARCIALMENTE_PROCEDENTE)}
        {chip("IMPROCEDENTE",            "Improcedência",           CONV_COLORS.IMPROCEDENTE)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8 items-center">
        <div className="flex justify-center md:justify-start">
          <FullPie counts={merged} focus={focus}/>
        </div>
        <div className="space-y-3">
          {[
            ["PROCEDENTE",              "Procedência"],
            ["PARCIALMENTE_PROCEDENTE", "Parcialmente procedente"],
            ["IMPROCEDENTE",            "Improcedência"],
            ["EXTINTO_SEM_MERITO",      "Extinto sem mérito"],
            ["NAO_IDENTIFICADO",        "Não identificado"],
          ].map(([k, label]) => {
            const v = merged[k] || 0;
            const p = pct(k);
            const dim = focus && focus !== k;
            return (
              <div key={k} data-testid={`conv-row-${k}`}
                   className="flex items-center gap-3 transition-opacity"
                   style={{ opacity: dim ? 0.35 : 1 }}>
                <span className="w-3 h-3 rounded-full shrink-0" style={{ background: CONV_COLORS[k] }}/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[13px] text-slate-700">{label}</span>
                    <span className="font-serif text-lg text-[#132238]">
                      <span className="tabular-nums">{p}%</span>
                      <span className="text-[11px] text-slate-500 ml-1">({v})</span>
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${p}%`, background: CONV_COLORS[k] }}/>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {total > 0 && (
        <div className="mt-6 rounded-xl border p-4 flex items-start gap-2 text-[12px]"
             style={{ borderColor: "#E7E5E2", background: "#FBF7EE" }} data-testid="conv-narrative">
          <Info className="w-3.5 h-3.5 mt-0.5" style={{ color: "#C9953D" }}/>
          <div className="text-slate-700 leading-snug">
            Da amostra combinada de <strong>{total}</strong> julgado{total===1?"":"s"},{" "}
            <strong style={{ color: CONV_COLORS.PROCEDENTE }}>{pct("PROCEDENTE")}%</strong> foram procedentes,{" "}
            <strong style={{ color: CONV_COLORS.PARCIALMENTE_PROCEDENTE }}>{pct("PARCIALMENTE_PROCEDENTE")}%</strong> parcialmente procedentes e{" "}
            <strong style={{ color: CONV_COLORS.IMPROCEDENTE }}>{pct("IMPROCEDENTE")}%</strong> improcedentes.
            {focus && (
              <> Filtro ativo: <strong style={{ color: CONV_COLORS[focus] }}>{CONV_LABEL[focus]}</strong>.</>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function ValePena() {
  const [tema, setTema] = useState("");
  const [state, setState] = useState("MG");
  const [ramo, setRamo] = useState("STATE");
  const [periodo, setPeriodo] = useState("-5a");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const tribunal = UF_TRIBUNAL[ramo]?.(state) || "";

  const analisar = async (e) => {
    e.preventDefault();
    if (!tema.trim() || tema.length < 3) { toast.error("Descreva melhor o tema/tese"); return; }
    setLoading(true);
    try {
      const { date_from } = dateBounds(periodo);
      const params = new URLSearchParams({ tema: tema.trim(), state, justice_branch: ramo, court: tribunal });
      if (date_from) params.set("date_from", date_from);
      const r = await api.get(`/jurisprudence/vale-a-pena?${params.toString()}`);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha na análise");
    } finally { setLoading(false); }
  };

  const l = data?.local; const s = data?.stj;
  const convColor = { ALTA:"#10B981", MODERADA:"#F59E0B", BAIXA:"#DC2626", DIVERGENTE:"#7C2D12", INDISPONIVEL:"#94a3b8" }[data?.convergencia || "INDISPONIVEL"];

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="vale-pena-page">
      <TopBar/>
      <div className="p-6 lg:p-8 w-full font-sans">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 mb-3">
        <ArrowLeft className="w-3.5 h-3.5"/> Voltar
      </Link>
      {/* Hero — imagem 4K institucional (slot: vale_pena_hero) */}
      <section className="relative rounded-2xl overflow-hidden border h-[340px] mb-6" style={{ borderColor: "#E7E5E2" }} data-testid="vale-pena-hero">
        <ImageSlot slot="vale_pena_hero" alt="Vale a Pena? · análise jurisprudencial" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(11,27,47,0.82) 0%, rgba(11,27,47,0.55) 40%, rgba(11,27,47,0.12) 78%, rgba(11,27,47,0) 100%)" }}/>
        <div className="relative h-full flex flex-col justify-center px-8 md:px-12 max-w-2xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl inline-flex items-center justify-center backdrop-blur" style={{ background: "rgba(240,211,122,0.18)", border: "1px solid rgba(240,211,122,0.55)" }}>
              <Scale className="w-5 h-5" style={{ color: "#F0D37A" }}/>
            </div>
            <div className="text-[11px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#F0D37A" }}>
              HERMES · Análise jurisprudencial
            </div>
          </div>
          <h1 className="font-serif text-5xl md:text-6xl leading-[1.02] tracking-tight text-white" style={{ textShadow: "0 2px 24px rgba(0,0,0,0.4)" }}>
            Vale a <span className="italic" style={{ color: "#F5DE8C" }}>Pena?</span>
          </h1>
          <p className="mt-4 font-serif italic text-lg text-white/85" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.45)" }}>
            "Decida com dados. Advogue com confiança."
          </p>
        </div>
      </section>

      <form onSubmit={analisar} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6" data-testid="vale-pena-form">
        <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Tema / tese / questão</label>
        <input value={tema} onChange={e=>setTema(e.target.value)} data-testid="input-tema"
          placeholder="ex: plano de saúde autismo terapia ABA"
          className="w-full h-12 px-4 rounded-xl border border-slate-200 mt-2 text-sm"/>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <div><label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Estado</label>
            <select value={state} onChange={e=>setState(e.target.value)} data-testid="select-state" className="w-full h-11 px-3 rounded-lg border border-slate-200 mt-1 text-sm">
              {ESTADOS.map(([c,n])=><option key={c} value={c}>{n}</option>)}
            </select>
          </div>
          <div><label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Ramo</label>
            <select value={ramo} onChange={e=>setRamo(e.target.value)} data-testid="select-ramo" className="w-full h-11 px-3 rounded-lg border border-slate-200 mt-1 text-sm">
              <option value="STATE">Justiça Estadual</option>
              <option value="FEDERAL">Justiça Federal</option>
              <option value="LABOR">Justiça do Trabalho</option>
              <option value="ELECT">Justiça Eleitoral</option>
            </select>
          </div>
          <div><label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Tribunal</label>
            <input readOnly value={tribunal} data-testid="input-tribunal" className="w-full h-11 px-3 rounded-lg border border-slate-200 bg-slate-50 mt-1 text-sm font-mono"/>
          </div>
          <div><label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Período</label>
            <select value={periodo} onChange={e=>setPeriodo(e.target.value)} data-testid="select-periodo" className="w-full h-11 px-3 rounded-lg border border-slate-200 mt-1 text-sm">
              {PERIODOS.map(([c,n])=><option key={c} value={c}>{n}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <div className="text-xs text-slate-600 flex items-center gap-2" data-testid="stj-always-on">
            <span className="w-2 h-2 rounded-full bg-emerald-500"/>
            <strong>STJ · Referência Nacional</strong> <span className="text-slate-400">Sempre ativo (não pode ser desligado)</span>
          </div>
          <div className="flex-1"/>
          <button type="submit" disabled={loading} data-testid="btn-analisar"
            className="h-11 px-6 rounded-xl font-semibold shadow-sm inline-flex items-center gap-2"
            style={{ background: "linear-gradient(180deg,#F5DE8C,#D4AF37)", color: "#0B1B2F" }}>
            <Search className="w-4 h-4"/> {loading ? "Analisando…" : "Analisar"}
          </button>
        </div>
      </form>

      {data && (<>
        {/* Aviso quando a base local ainda não tem julgados classificados */}
        {(!l?.total || l?.total === 0) && (
          <div className="rounded-2xl border p-4 mb-4 flex items-start gap-3 bg-amber-50 border-amber-200 text-amber-900" data-testid="vale-pena-empty-local">
            <Info className="w-4 h-4 mt-0.5 shrink-0"/>
            <div className="text-[13px] leading-snug">
              <strong>Base local ({tribunal}) ainda em ingestão.</strong> Estamos importando os julgados desse tribunal — nesse momento
              a análise de <em>Possibilidade de Procedência</em> só está calibrada para o STJ. O painel do STJ à direita segue funcional
              e mostra a jurisprudência nacional sobre o tema.
            </div>
          </div>
        )}
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="vale-pena-kpis">
          <div className="rounded-2xl p-5 text-white shadow-sm" style={{ background: "linear-gradient(135deg,#0B1B2F,#071B34)" }}>
            <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "#F0D37A" }}>Possibilidade de Procedência</div>
            <div className="font-heading text-4xl font-bold mt-1" style={{ color: "#F5DE8C" }}>{l?.possibilidade_procedencia_pct != null ? `${l.possibilidade_procedencia_pct}%` : "—"}</div>
            <div className="text-[11px] mt-1 opacity-80">Base: {l?.total || 0} julgados · {tribunal}</div>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Julgados localizados</div>
            <div className="font-heading text-3xl font-bold text-slate-900 mt-1">{l?.total || 0}</div>
            <div className="text-[11px] text-slate-500 mt-1">{l?.classified || 0} classificados</div>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Cobertura</div>
            <div className="font-heading text-3xl font-bold text-slate-900 mt-1">{l?.coverage_pct ?? 0}%</div>
            <div className="text-[11px] text-slate-500 mt-1">Confiabilidade: <strong>{l?.sample_confidence || "—"}</strong></div>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Convergência</div>
            <div className="font-heading text-2xl font-bold mt-1" style={{ color: convColor }}>{data.convergencia}</div>
            <div className="text-[11px] text-slate-500 mt-1">STJ ⇔ {tribunal}</div>
          </div>
        </div>

        {/* CONVERGÊNCIA COM A JURISPRUDÊNCIA (visão unificada + filtros + pizza) */}
        <ConvergenceCard local={l} stj={s} tribunal={tribunal}/>

        {/* Painéis separados */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="panel-local">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4 text-slate-500"/>
              <div className="font-heading text-base font-semibold text-slate-900">Resultado da ação · {tribunal}</div>
            </div>
            <Donut counts={l?.merits_counts} testid="donut-local"/>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="panel-stj">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4 text-slate-500"/>
              <div className="font-heading text-base font-semibold text-slate-900">Resultado recursal · STJ</div>
            </div>
            <Donut counts={s?.appeal_counts} testid="donut-stj"
                   palette={{ PROVIDO:"#10B981", PARCIALMENTE_PROVIDO:"#F59E0B", DESPROVIDO:"#DC2626", NAO_CONHECIDO:"#7C2D12", PREJUDICADO:"#94a3b8", NAO_IDENTIFICADO:"#e2e8f0" }}/>
            <div className="mt-3 text-xs text-slate-500">STJ: {s?.total || 0} julgados relacionados · sempre incluído</div>
          </div>
        </div>

        {/* Tendência */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-slate-500"/>
            <div className="font-heading text-base font-semibold text-slate-900">Tendência anual · Possibilidade de Procedência</div>
          </div>
          <YearlyBars series={l?.yearly}/>
        </div>

        {/* Rodapé auditabilidade */}
        <div className="rounded-2xl border border-amber-200/60 p-4 text-xs text-slate-600 flex items-start gap-2" style={{ background: "linear-gradient(90deg,#FFFBEA,#FFF7D6)" }}>
          <Info className="w-3.5 h-3.5 mt-0.5 text-amber-700"/>
          <div>
            <strong>Fórmula:</strong> possibilidade = (procedentes × 1,0 + parciais × 0,5) ÷ classificados. Amostra: {l?.total} · Classificados: {l?.classified} · Não identificados: {(l?.total||0)-(l?.classified||0)}. Fonte: banco jurisprudencial HERMES.
          </div>
        </div>
      </>)}
      </div>
    </div>
  );
}
